import '../styles/websiteBuilder.css';

const features = [
    {
        title: 'Drag-and-drop sections',
        description: 'Choose from hero, fleet, testimonial, and pricing sections that automatically inherit your brand colors and typography.',
        stat: '25+ ready-made blocks',
    },
    {
        title: 'Booking-first layouts',
        description: 'Every template keeps your inventory, insurance add-ons, and checkout flows above the fold so visitors convert on mobile and desktop.',
        stat: '+34% average conversions',
    },
    {
        title: 'Live inventory sync',
        description: 'Publish once and keep vehicle availability updated in real time from Fleetify - no double entry or spreadsheet uploads required.',
        stat: 'Syncs every 60 seconds',
    },
    {
        title: 'SEO and analytics baked in',
        description: 'Auto-generate SEO tags, location landing pages, and send events to GA4 and Meta without touching code.',
        stat: 'Ready for multi-location',
    },
];

const templates = [
    {
        name: 'City Drive',
        type: 'Modern multi-location',
        badges: ['Best for franchises', 'Supports blog'],
        description: 'Bold hero, sticky booking widget, and sections for fleet highlights plus reviews.',
    },
    {
        name: 'Coastal Classic',
        type: 'Premium concierge',
        badges: ['High-end fleets', 'Long term'],
        description: 'Editorial layout with story-driven copy, perfect for premium SUVs and exotic vehicles.',
    },
    {
        name: 'Express Fleet',
        type: 'High volume',
        badges: ['Airport pickup', 'Walk-up ready'],
        description: 'Compact cards, instant WhatsApp chat, and integrated FAQ for fast-moving renters.',
    },
];

const steps = [
    {
        title: 'Set your brand system',
        detail: 'Upload your logo, pick two colors, and we auto-build buttons, gradients, and typography.',
    },
    {
        title: 'Connect inventory + add-ons',
        detail: 'Choose which vehicles, upsells, and insurance packages should appear on each page.',
    },
    {
        title: 'Publish anywhere',
        detail: 'Deploy to a Fleetify subdomain or export a lightweight React bundle to host on your own domain.',
    },
];

const faqs = [
    {
        question: 'Can I use my existing domain?',
        answer: 'Yes. Point your DNS at Fleetify or download a static export that you can drop into any hosting provider.',
    },
    {
        question: 'How customizable are the templates?',
        answer: 'Every block supports swapping media, layout density, button style, and typography scale. You can also inject custom CSS.',
    },
    {
        question: 'Does the builder support multiple languages?',
        answer: 'You can create localized versions of each page, sync fleet data per language, and toggle them live per market.',
    },
];

function WebsiteBuilder() {
    return (
        <div className="builder-page">
            <section className="builder-hero">
                <div className="container builder-hero-inner">
                    <div className="builder-hero-copy">
                        <p className="eyebrow">Website Builder</p>
                        <h1>Create a high-converting rental site in under an hour</h1>
                        <p className="subtitle">
                            Launch responsive pages that plug directly into Fleetify inventory, pricing, and automations.
                            No freelancers, no custom code - just pick a template and publish.
                        </p>
                        <div className="builder-metrics">
                            <div>
                                <span className="metric-value">7 min</span>
                                <span className="metric-label">to publish updates</span>
                            </div>
                            <div>
                                <span className="metric-value">99.9%</span>
                                <span className="metric-label">uptime with CDN</span>
                            </div>
                            <div>
                                <span className="metric-value">SEO</span>
                                <span className="metric-label">structured data ready</span>
                            </div>
                        </div>
                        <div className="builder-hero-actions">
                            <a href="/website-builder/live-demo" className="btn btn-primary">Live Demo</a>
                            <a href="/pricing" className="btn btn-outline">Compare Plans</a>
                        </div>
                        <p className="hero-footnote">Public beta - Included in Growth & Enterprise</p>
                    </div>
                    <div className="builder-hero-media">
                        <div className="browser-chrome">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                        <img src="/images/dashboard.png" alt="Fleetify website builder preview" />
                        <div className="builder-hero-card">
                            <p>Drag sections, preview mobile, and hit publish. We will handle hosting, SSL, and analytics.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="builder-live-demo">
                <div className="container builder-live-grid">
                    <div className="builder-live-copy">
                        <p className="eyebrow">Live builder demo</p>
                        <h2>See the website builder in action</h2>
                        <p>
                            Layouts, sections, and style controls update instantly. Toggle brand colors, swap templates, and publish
                            to a Fleetify domain or your own - no engineers required.
                        </p>
                        <ul className="live-demo-list">
                            <li>Drag pre-built hero, fleet, pricing, and testimonial blocks</li>
                            <li>Apply brand kit and CTA styles across every page</li>
                            <li>Preview desktop and mobile before you publish</li>
                        </ul>
                        <div className="builder-hero-actions">
                            <a href="/book-demo" className="btn btn-primary">Book a live session</a>
                            <a href="/pricing" className="btn btn-outline">Start free trial</a>
                        </div>
                    </div>
                    <div className="builder-live-preview">
                        <div className="builder-live-sidebar">
                            <div className="sidebar-title">Sections</div>
                            <button className="sidebar-chip active">Hero</button>
                            <button className="sidebar-chip">Fleet grid</button>
                            <button className="sidebar-chip">Pricing</button>
                            <button className="sidebar-chip">Testimonials</button>
                            <button className="sidebar-chip">FAQ</button>
                            <button className="sidebar-chip">Contact</button>
                        </div>
                        <div className="builder-live-canvas">
                            <div className="builder-live-toolbar">
                                <span className="dot"></span>
                                <span className="dot"></span>
                                <span className="dot"></span>
                                <div className="toolbar-actions">
                                    <span className="toolbar-chip">Desktop</span>
                                    <span className="toolbar-chip muted">Mobile</span>
                                </div>
                            </div>
                            <div className="preview-hero">
                                <div className="preview-text">
                                    <p className="preview-eyebrow">Car Rental</p>
                                    <h3>Launch your rental site faster</h3>
                                    <p className="preview-body">Publish a branded booking page with live rates and availability pulled from Fleetify.</p>
                                    <div className="preview-actions">
                                        <span className="btn btn-primary btn-small">Book now</span>
                                        <span className="btn btn-outline btn-small">View fleet</span>
                                    </div>
                                </div>
                                <div className="preview-card">
                                    <div className="preview-tag">Live availability</div>
                                    <div className="preview-vehicle">
                                        <div>
                                            <strong>Premium SUV</strong>
                                            <p>$119/day · In stock</p>
                                        </div>
                                        <span className="btn btn-primary btn-tiny">Select</span>
                                    </div>
                                    <div className="preview-vehicle">
                                        <div>
                                            <strong>Compact</strong>
                                            <p>$59/day · In stock</p>
                                        </div>
                                        <span className="btn btn-primary btn-tiny">Select</span>
                                    </div>
                                </div>
                            </div>
                            <div className="preview-footer">
                                Drag sections to reorder · Changes are autosaved · Preview before publish
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="builder-features">
                <div className="container">
                    <div className="section-header">
                        <p className="eyebrow">Everything you need</p>
                        <h2>Purpose-built for vehicle rental teams</h2>
                        <p>Use Fleetify data to power landing pages, franchise hubs, partner microsites, and seasonal campaigns.</p>
                    </div>
                    <div className="feature-grid">
                        {features.map((feature) => (
                            <article className="feature-card" key={feature.title}>
                                <div className="feature-stat">{feature.stat}</div>
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="builder-templates">
                <div className="container">
                    <div className="section-header">
                        <p className="eyebrow">Template gallery</p>
                        <h2>Pick a starting point, personalize everything</h2>
                    </div>
                    <div className="template-grid">
                        {templates.map((template) => (
                            <article className="template-card" key={template.name}>
                                <div className="template-card-head">
                                    <h3>{template.name}</h3>
                                    <span>{template.type}</span>
                                </div>
                                <p>{template.description}</p>
                                <div className="template-badges">
                                    {template.badges.map((badge) => (
                                        <span key={badge}>{badge}</span>
                                    ))}
                                </div>
                                <button className="btn btn-outline btn-small">Preview layout</button>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="builder-flow">
                <div className="container builder-flow-inner">
                    <div className="section-header">
                        <p className="eyebrow">Go live in three steps</p>
                        <h2>From idea to published site without leaving Fleetify</h2>
                        <p>Every workflow has guardrails so your brand stays on-point while non-technical teammates can still contribute.</p>
                    </div>
                    <ol className="flow-steps">
                        {steps.map((step, index) => (
                            <li key={step.title}>
                                <div className="step-index">{index + 1}</div>
                                <div>
                                    <h3>{step.title}</h3>
                                    <p>{step.detail}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <section className="builder-faq">
                <div className="container">
                    <div className="section-header">
                        <p className="eyebrow">FAQ</p>
                        <h2>Answers before you launch</h2>
                    </div>
                    <div className="faq-grid">
                        {faqs.map((faq) => (
                            <article key={faq.question}>
                                <h3>{faq.question}</h3>
                                <p>{faq.answer}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="builder-cta">
                <div className="container builder-cta-inner">
                    <div>
                        <p className="eyebrow">Launch-ready today</p>
                        <h2>Turn Fleetify data into a marketing site that stays up to date automatically.</h2>
                    </div>
                    <div className="builder-cta-actions">
                        <a href="/book-demo" className="btn btn-primary">See it in action</a>
                        <a href="/pricing" className="btn btn-outline">Start free trial</a>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default WebsiteBuilder;
